import { Fragment } from 'react';
import { useParams } from 'react-router-dom';

import axios from 'axios';

import { FormClosed } from '@components/form-closed';
import { Spinner } from '@components/loading-spinner';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { TooltipStyles } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { DatePickerStyles } from '@styles/datepicker';

import { useJourneyForm, useSaveJourneyForm } from 'api/form';

import { DisplayForm } from './display-form';
import { JourneyFormLoadError } from './journey-form-load-error';
import { SubmissionSuccess } from './submission-success';

type RouteParams = {
  journeyUuid: string;
  formUuid: string;
};

export const JourneyForm = () => {
  const { t } = useScopedTranslate(translations);
  const { formUuid, journeyUuid } = useParams() as RouteParams;
  const formQuery = useJourneyForm(journeyUuid, formUuid);
  const saveMutation = useSaveJourneyForm(journeyUuid, formUuid);
  const showSubmittingSpinner = useRotatingLoadingMessages({
    title: t('justAMoment'),
    isEnabled: saveMutation.isLoading,
    messages: [t('submittingInfo')],
  });

  const isFormClosedError =
    saveMutation.isError &&
    axios.isAxiosError(saveMutation.error) &&
    saveMutation.error.response?.status === 400 &&
    saveMutation.error.response?.data?.error === 'form_closed';

  const formClosedErrorData = isFormClosedError ? (saveMutation.error as any).response?.data : null;
  const formClosedReason = formClosedErrorData?.reason ?? null;

  const showSubmissionSuccessPage = saveMutation.isSuccess && !showSubmittingSpinner;
  const showDisplayForm =
    formQuery.isSuccess &&
    !formQuery.data.isClosed &&
    !isFormClosedError &&
    (!saveMutation.isSuccess || showSubmittingSpinner);

  if (formQuery.isLoading && !showSubmittingSpinner) {
    return <Spinner />;
  }

  if (!formQuery.data?.hasPublishedJourney) {
    return <div></div>;
  }

  if (formQuery.data.isClosed) {
    return (
      <FormClosed
        closedHeading={formQuery.data.closedHeading}
        closedMessage={formQuery.data.closedMessage}
        closeReason={formQuery.data.closeReason}
      />
    );
  }

  if (isFormClosedError) {
    return (
      <FormClosed
        closedHeading={formClosedErrorData?.closedHeading}
        closedMessage={formClosedErrorData?.closedMessage}
        closeReason={formClosedReason}
      />
    );
  }

  return (
    <Fragment>
      <DatePickerStyles />
      <TooltipStyles />
      {showSubmissionSuccessPage && (
        <SubmissionSuccess submissionMessageConfig={formQuery.data?.submissionMessageConfig} />
      )}

      {showDisplayForm && (
        <DisplayForm
          formConfig={formQuery.data.fieldsConfig}
          lists={formQuery.data.lists}
          onSubmit={saveMutation.mutateAsync}
          hideSubmitSpinner
        />
      )}

      {formQuery.isError && <JourneyFormLoadError />}
    </Fragment>
  );
};

const translations = {
  en: {
    justAMoment: 'Just a moment',
    submittingInfo: 'Submitting information...',
  },
  is: {
    justAMoment: 'Augnablik',
    submittingInfo: 'Sendi upplýsingar...',
  },
};
